const GRID_GAP = 8;
const GRID_PADDING = 8;

// Minimum feasible dimensions for tiles
const MIN_FEASIBLE_WIDTH = 110;
const MIN_FEASIBLE_HEIGHT = 90;
const MIN_FEASIBLE_RATIO = 9 / 21; // Approximately 0.429

interface ScreenshareGridDimensions {
  cols: number;
  rows: number;
  tileHeight: number;
  tileWidth: number;
  maxTiles: number;
}

export const fitToScreenshareGrid = (
  width: number,
  height: number,
  tiles: number
): ScreenshareGridDimensions => {
  if (tiles <= 0) {
    return {
      cols: 1,
      rows: 1,
      tileWidth: width - 2 * GRID_PADDING,
      tileHeight: height - 2 * GRID_PADDING,
      maxTiles: 1,
    };
  }

  const availableWidth = width - 2 * GRID_PADDING;
  const availableHeight = height - 2 * GRID_PADDING;

  // Maximum number of columns and rows to consider
  const maxCols = 7;
  const maxRows = 7;

  // Maximum number of tiles we can potentially fit on screen
  const maxRenderAreaTiles = Math.floor(
    (availableWidth * availableHeight) / (MIN_FEASIBLE_WIDTH * MIN_FEASIBLE_HEIGHT)
  );

  // Establish upper limit of tiles that need to be fitted
  let approxMaxTiles = Math.min(maxCols * maxRows, tiles, maxRenderAreaTiles);

  let cols = 1;
  let rows = 1;
  let fitted = 1;

  // Incrementally add rows or columns until we can't fit any more tiles
  while (fitted < approxMaxTiles) {
    const newRows = rows + 1;
    const newCols = cols + 1;

    // Calculate dimensions for growing horizontally
    const growHorizontallyWidth = (availableWidth - (newCols - 1) * GRID_GAP) / newCols;
    const canGrowHorizontally = MIN_FEASIBLE_WIDTH <= growHorizontallyWidth && cols < maxCols;

    // Calculate dimensions for growing vertically
    const growVerticallyHeight = (availableHeight - (newRows - 1) * GRID_GAP) / newRows;
    const growVerticallyRatio =
      growVerticallyHeight / ((availableWidth - (cols - 1) * GRID_GAP) / cols);
    const canGrowVertically =
      MIN_FEASIBLE_HEIGHT <= growVerticallyHeight &&
      growVerticallyRatio >= MIN_FEASIBLE_RATIO &&
      rows < maxRows;

    if (!(canGrowHorizontally || canGrowVertically)) {
      approxMaxTiles = cols * rows;
      fitted = approxMaxTiles;
    } else {
      if (canGrowHorizontally && !canGrowVertically) {
        cols = newCols;
      } else if (!canGrowHorizontally && canGrowVertically) {
        rows = newRows;
      } else {
        // If we can grow in both directions, choose the one that gives better area utilization
        const containerAspectRatio = availableWidth / availableHeight;

        // Prefer growing in the direction that better matches the container aspect ratio
        if (newCols / rows > containerAspectRatio && newRows / cols <= containerAspectRatio) {
          rows = newRows;
        } else if (
          newCols / rows <= containerAspectRatio &&
          newRows / cols > containerAspectRatio
        ) {
          cols = newCols;
        } else {
          // If both directions would result in similar aspect ratios, prefer the one that gives more square tiles
          const growHorizontallyTileRatio = growHorizontallyWidth / growVerticallyHeight;
          const growVerticallyTileRatio =
            (availableWidth - (cols - 1) * GRID_GAP) / cols / growVerticallyHeight;

          const horizontalSquareness = Math.abs(growHorizontallyTileRatio - 1);
          const verticalSquareness = Math.abs(growVerticallyTileRatio - 1);

          if (verticalSquareness < horizontalSquareness) {
            rows = newRows;
          } else {
            cols = newCols;
          }
        }
      }

      fitted = cols * rows;
    }
  }

  // Ensure we have at least one row and column
  approxMaxTiles = approxMaxTiles || 1;
  rows = Math.ceil(approxMaxTiles / cols) || 1;

  // Calculate final tile dimensions
  const tileWidth = (availableWidth - (cols - 1) * GRID_GAP) / cols;
  const tileHeight = (availableHeight - (rows - 1) * GRID_GAP) / rows;

  return {
    tileHeight,
    tileWidth,
    cols,
    rows,
    maxTiles: cols * rows,
  };
};
